import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import { BottomModal } from '../../Modal';
import { fetchTicket } from '../../../../../ducks/user/user';
import { getCheckinCode } from '../helpers';
import Flash from '../../Flash';
import Input from '../../../../Account/components/Input';
import dhid from '../../../../../dhid';
import styles from './fetch-modal.scss';

const propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  dispatch: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

class FetchModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      order: localStorage.getItem('order'),
      email: localStorage.getItem('email'),
      data: null,
      errors: {},
      error: undefined,
    };
  }

  onInputChange = (event, field) => {
    this.setState({
      [field]: event.target.value,
    });
    localStorage.setItem(field, event.target.value);
  }

  onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { order, email } = this.state;

    this.setState({
      error: undefined,
      data: null,
      errors: {},
      disable: true,
    });

    dhid.post('1/content/tickets/fetch', { order, email })
      .then((resp) => {
        this.setState({ data: resp.data.result, errors: {} });
      })
      .catch((resp) => {
        if (resp.status === 422) {
          this.setState({ errors: resp.json.error, data: null });
        } else if (resp.json) {
          this.setState({ error: resp.json.error });
        } else {
          this.setState({ error: resp.message });
        }
      }).finally(() => {
        this.setState({ disable: false });
      });
  }

  onFetchTicket = (ticket) => {
    const { user, dispatch } = this.props;
    const { order, email } = this.state;

    this.setState({
      disable: true,
      fetchingId: ticket.id,
    });

    if (
      (ticket.claimed_by && ticket.claimed_by === user.id)
      || ticket.order.claimed_by === user.id
    ) {
      this.setState({ error: undefined }, () => {
        this.setState({ error: 'You\'ve already added this ticket.' });
      });
      return;
    } else if (ticket.claimed_by) {
      this.setState({ error: undefined }, () => {
        this.setState({ error: `This ticket is already added by ${ticket.ticketHolder}.` });
      });
      return;
    }

    dispatch(fetchTicket(order, email, ticket.id))
      .then((resp) => {
        this.setState({ data: resp.data.result, errors: {} });
      })
      .catch((resp) => {
        if (resp.status === 422) {
          this.setState({ errors: resp.json.error, data: null });
        } else if (resp.json) {
          this.setState({ error: resp.json.error });
        } else {
          this.setState({ error: resp.message });
        }
      }).finally(() => {
        this.setState({ disable: false, fetchingId: null });
      });
  }

  // Not only close the modal, but clear the data so that it can be used if opened again
  closeModal = () => {
    this.props.onRequestClose();
    setTimeout(() => (
      this.setState({
        data: null,
      })
    ), 200); // Wait for the modal to close!
  }

  render() {
    const {
      isOpen,
      user,
      loading,
    } = this.props;
    const {
      data,
      disable,
      email,
      error,
      errors,
      order,
      fetchingId,
    } = this.state;

    const sortedTickets = data && Object.values(data).sort((a, b) => {
      // Sort by event.date_start then event.name, seat, byoc>non byoc and lastly ticket.id
      // This is important to keep the ticket list the same after re-render

      if (a?.ticket_type?.event && b?.ticket_type?.event) {
        const byStartDate = moment(b.ticket_type.event.local_start_time)
          .diff(moment(a.ticket_type.event.local_start_time));
        if (byStartDate !== 0) { return byStartDate; }

        const byEventName = a.ticket_type.event.name.localeCompare(b.ticket_type.event.name);
        if (byEventName !== 0) { return byEventName; }
      }

      if (a.expanded_seat && b.expanded_seat) {
        const byFloor = a.expanded_seat
          && b.expanded_seat
          && typeof a.expanded_seat.expanded_row.expanded_section.floor.name !== 'undefined'
          && typeof b.expanded_seat.expanded_row.expanded_section.floor.name !== 'undefined'
          && a.expanded_seat.expanded_row.expanded_section.floor.name.localeCompare(
            b.expanded_seat.expanded_row.expanded_section.floor.name,
          );
        if (byFloor !== 0 && byFloor !== false) { return byFloor; }

        const aSeat = `${a.expanded_seat.expanded_row.name}:${a.expanded_seat.name}`;
        const bSeat = `${b.expanded_seat.expanded_row.name}:${b.expanded_seat.name}`;
        const bySeat = aSeat.localeCompare(bSeat);
        if (bySeat !== 0) { return bySeat; }
      }

      // Make sure BYOC tickets are on top
      if (a.expanded_seat !== b.expanded_seat) {
        return (b.expanded_seat && 1) - (a.expanded_seat && 1);
      }

      return a.id.localeCompare(b.id);
    });

    return (
      <BottomModal
        isOpen={isOpen}
        onRequestClose={() => this.closeModal()}
        buttonText="Close"
        {...(!data ? {
          action: {
            text: 'Continue',
            onClick: this.onSubmit,
            disabled: disable,
          },
        } : {}
        )}
      >
        <div className={styles.container}>
          <Flash
            title="Whoops!"
            message={error}
            error
          />
          <div className={styles.title}>
            {(data
              ? `${sortedTickets.length} ${sortedTickets.length > 1 ? 'tickets' : 'ticket'} found`
              : 'Add a missing ticket'
            )}
          </div>
          {!data && (
          <form
            className={styles.form}
            onSubmit={this.onSubmit}
          >
            <Input
              label="Order or ticket reference"
              placeholder="Order or ticket reference"
              explanation="Found on your ticket or receipt"
              id="order"
              type="text"
              required
              onChange={(event) => this.onInputChange(event, 'order')}
              value={order}
              errors={errors}
              disabled={disable}
            />
            <Input
              label="Email address"
              placeholder="Email address"
              explanation="The email address used at the time of purchase"
              id="email"
              type="email"
              required
              onChange={(event) => this.onInputChange(event, 'email')}
              value={email}
              errors={errors}
              disabled={disable}
            />
            <input type="submit" style={{ display: 'none' }} />
          </form>
          )}
          { sortedTickets && (
            <div className={styles.tickets}>
              {sortedTickets.map((ticket) => {
                const ticketCode = getCheckinCode(ticket) || { text: ticket.id, provider: 'DreamHack' };
                const addedByElse = ticket.claimed_by
                  && ticket.claimed_by !== user.id && ticket.order.claimed_by !== user.id;
                const addedByMe = ((ticket.claimed_by && ticket.claimed_by === user.id)
                  || ticket.order.claimed_by === user.id);

                return (
                  <div
                    className={classNames(
                      styles.ticket,
                      ticket.claimed_by && styles.disabled,
                    )}
                    key={ticket.id}
                  >
                    <div className={styles.info}>
                      <div className={styles.type}>
                        { ticket.ticket_type && ticket.ticket_type.name }
                      </div>
                      <div className={styles.name}>
                        {ticket.ticketHolder || '-'}
                      </div>
                      <div className={styles.event}>
                        { ticket.ticket_type
                          && (
                            <span>
                              <i className="far fa-calendar-alt" />
                              {' '}
                              {ticket.ticket_type.event.name}
                              {' '}
                            </span>
                          )}
                      </div>
                      <div className={styles.seat}>
                        { ticket.expanded_seat
                          && (
                            <>
                              <i className="far fa-map" />
                              {' '}
                              {
                              // If we're looking at a ticket for montreal 19 - use section name
                              // as floor name.
                              (ticket.ticket_type.event && ticket.ticket_type.event.id === 'e04cdf59-11b4-4040-bb87-c25ca8678851')
                                ? ticket.expanded_seat.expanded_row.expanded_section.name
                                : ticket.expanded_seat.expanded_row.expanded_section.floor.name
                            }
                              {' '}
                              <i className="far fa-map-marker" />
                              {' '}
                              {`${ticket.expanded_seat.expanded_row.name}:${ticket.expanded_seat.name}`}
                            </>
                          )}
                      </div>
                      <div className={styles.code}>
                        {ticketCode.text}
                      </div>
                      { addedByMe && (
                        <div className={classNames(styles.status, styles.addedByMe)}>
                          {'You\'ve added this ticket!'}
                        </div>
                      )}
                      { addedByElse && (
                        <div className={classNames(styles.status, styles.addedByElse)}>
                          {`This ticket is already added by ${ticket.ticketHolder}.`}
                        </div>
                      )}
                    </div>
                    <div
                      className={styles.control}
                      onClick={() => this.onFetchTicket(ticket)}
                      role="button"
                      tabIndex="0"
                      disabled={disable}
                    >
                      { fetchingId === ticket.id && <i className={classNames('far fa-spinner-third fa-spin', styles.rotate)} /> }
                      {!loading && !ticket.claimed_by && !(ticket.order.claimed_by === user.id) && (
                        <i
                          className="far fa-plus-circle"
                        />
                      )}
                      { addedByElse && (
                        <i className={classNames('far fa-times', styles.addedByElse)} />
                      )}
                      { addedByMe && (
                        <i className={classNames('far fa-check', styles.addedByMe)} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </BottomModal>
    );
  }
}

FetchModal.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.getIn(['user', 'loading']),
  };
}

export default connect(mapStateToProps)(FetchModal);
